import { Box, Typography } from '@mui/material';

import { Iconify } from 'src/components/iconify';

type Props = {
  value: number | null | undefined;
  caption: string;
  lang: string;
  /**
   * What the figure is a change in. A percentage by default; NAT-2579's margin % states its
   * change in points (" pt"), because a percentage of a percentage misreads.
   */
  suffix?: string;
  /**
   * NAT-2585 — a change that is neither good nor bad news (a rise in concentration): the arrow and
   * the figure stay, the green and the red do not.
   */
  neutral?: boolean;
};

/**
 * NAT-1713 — the change against a comparison span. Shared by the Termometro (Operativa and
 * «Situazione attuale») and «Statistiche vendite» (NAT-2579).
 *
 * `caption` names the span rather than hard-coding it, because a bare "+12%" does not say what
 * it is 12% of.
 *
 * `null` means the comparison span was zero, which has no percentage: growth from nothing is
 * undefined, not 100%, and rendering it as a number would invent a measurement. `undefined`
 * means the backend shipped no comparison — either it predates the field, or that leg of the
 * fetch failed and was degraded away. Both simply render nothing, because an absent comparison
 * is not a story about the business.
 */
/** The change as the caption prints it — «+12,5» — without its suffix. One definition, shared. */
export const formatDeltaValue = (value: number, lang: string) =>
  new Intl.NumberFormat(lang === 'en' ? 'en-US' : 'it-IT', {
    maximumFractionDigits: 1,
    signDisplay: 'exceptZero',
  }).format(value);

export default function DeltaCaption({
  value,
  caption,
  lang,
  suffix = '%',
  neutral = false,
}: Props) {
  if (value === null || value === undefined) return null;

  // Zero counts as neither a rise nor a fall, but it still needs an arrow and a colour; it
  // sits with the positives, matching the `signDisplay` below which renders it as a bare 0%.
  const rising = value >= 0;
  const formatted = formatDeltaValue(value, lang);
  let tone = rising ? 'success.main' : 'error.main';
  if (neutral) tone = 'text.secondary';

  return (
    <Typography
      variant="caption"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        // The arrow and the figure carry the colour together — a coloured number beside a
        // grey arrow reads as two separate signals. The trailing label stays secondary so the
        // colour marks the measurement, not the sentence.
        color: tone,
      }}
    >
      <Iconify
        icon={rising ? 'eva:trending-up-fill' : 'eva:trending-down-fill'}
        width={14}
        aria-hidden
      />
      {formatted}
      {suffix}
      <Box component="span" sx={{ color: 'text.secondary' }}>
        {caption}
      </Box>
    </Typography>
  );
}
